import { useMutation } from '@tanstack/react-query';
import { endpoints } from '@rossum/api-client';
import { SchemaSection } from '@rossum/api-client/schemas';
import { useApiClient } from '../../../../../lib/apiClient';
import type { LookupResult } from './lookupResult';
import { lookupResultSchema } from './lookupResult';
import { customMatchingConfigurationSchema } from './utils';

export type SuggestLookupVariables = {
  hint: string;
  queryIndex: number | null;
  // Carried in the variables rather than closed over, so a suggestion cannot be parsed
  // against a dataset the editor has since changed away from.
  dataset: string;
};

const MALFORMED_SUGGESTION =
  'The lookup suggester returned a response we cannot apply.';

/**
 * The lookup half of `suggest_computed_field`. It shares an endpoint with the formula
 * suggester and nothing else: a lookup response is only usable once it parses into a
 * dataset, queries and variables, so the parse belongs here where a bad response can
 * reject the promise instead of resolving into a success the caller has to second-guess.
 */
export const useSuggestLookup = ({
  fieldSchemaId,
  schemaContent,
}: {
  fieldSchemaId: string;
  schemaContent: SchemaSection[];
}) => {
  const api = useApiClient();

  return useMutation<LookupResult, unknown, SuggestLookupVariables>(
    async ({ hint, queryIndex, dataset }) => {
      const response = await api.request(
        endpoints.internal.schemas.suggestComputedField({
          schemaContent,
          fieldSchemaId,
          hint,
          iterative: queryIndex !== null,
          queryIndex,
        })
      );

      const field = response.results[0];
      if (!field) throw new Error(MALFORMED_SUGGESTION);

      const matching = customMatchingConfigurationSchema.safeParse(
        field.matching
      );
      if (!matching.success) throw new Error(MALFORMED_SUGGESTION);

      // Dataset stays as the editor has it: this path only changes queries and variables.
      const parsed = lookupResultSchema.safeParse({
        ...matching.data.configuration,
        dataset,
      });
      if (!parsed.success) throw new Error(MALFORMED_SUGGESTION);

      return parsed.data;
    }
  );
};
